/**
 * @file     compute-groups/authorization.js
 *           Code relating to requesting authorization for compute group actions. Mostly adapted from
 *           code in Supervisor2's Protocol Manager.
 * @author   Wes Garland, wes@distributive.network
 * @author   Paul Ringseth, paul@distributive.network
 * @date     March 2025
 */
'use strict';

const constants = require('dcp/common/scheduler-constants');
const hash      = require('dcp/common/hash');
const utils     = require('dcp/utils');

exports.generateTaskRequestWorkerComputeGroups = generateTaskRequestWorkerComputeGroups;
exports.calculateJoinHash = calculateJoinHash;

/**
 * Calculate a joinHash for a compute group.  This is an eh1- hash of the cg salt and
 * joinSecret components of a compute group description.
 *
 * @param {object}      details         an object containing the cg salt, which is
 *                                      the joinKey if the compute group uses one;
 *                                      otherwise it is the joinAddress. This object
 *                                      may also contain the joinSecret.
 * @param {string}      [joinSecret]    the join secret -- plain text -- that is
 *                                      the "password" for the compute group. If not
 *                                      specified, we use details.joinSecret.
 */
function calculateJoinHash(details, joinSecret)
{
  if (typeof joinSecret === 'undefined')
    joinSecret = details.joinSecret;

  return hash.calculate(hash.eh1, `${details.joinKey || details.joinAddress} ${joinSecret}`);
}

/**
 * Generate the workerComputeGroups property of the requestTask message.
 *
 * Concatenate the compute groups object from dcpConfig with the list of compute groups
 * from the supervisor, and remove the public group if accidentally present. Finally,
 * we transform joinSecrets/joinHash into joinHashHash for secure transmission.
 *
 * @param {object} workerConfig      an object with optional properties computeGroups and
 *                                   leaveGlobalGroup or leavePublicGroup (obsolete name), which define
 *                                   compute group membership for a worker. The global (formerly
 *                                   public) group is automatically added to the list unless it is
 *                                   explicitly disabled.
 *
 * @param {object|false} request     the dcp5::Connection()::Request that will be sent to the task
 *                                   distributor, or calse to skip hash step (useful for introspection).
 *
 * @returns {object[]}
 */
function generateTaskRequestWorkerComputeGroups(workerConfig, request)
{
  const computeGroups = Object.values(workerConfig.computeGroups || {}); /* config can be Object or Array */
  const hashedComputeGroups = [];
  var leaveProp;

  // Workers should be part of the global (formerly public) compute group by default.
  if (workerConfig.hasOwnProperty('leavePublicGroup'))
    leaveProp = 'leavePublicGroup'; /* deprecated API /wg mar 2025 */
  else
    leaveProp = 'leaveGlobalGroup';

  if (!utils.booley(workerConfig[leaveProp]))
  {
    computeGroups.push({
      joinKey: constants.computeGroups.public.joinKey,
      joinHash: constants.computeGroups.public.joinHash,
    });
  }

  if (request === false)
    return computeGroups;

  const dcpsid = request.connection.dcpsid;
  for (const group of computeGroups)
  {
    if (group.joinAddress)
      throw new Error('join by address not implemented');
    else
    {
      const joinHash = group.joinHash
            ? group.joinHash.replace(/\s+/g, '')       /* config supplied joinHash */
            : calculateJoinHash(group);                /* config supplied joinSecret (clear text) */
      const joinHashHash = hash.calculate(hash.eh1, joinHash, dcpsid);
      hashedComputeGroups.push({ joinKey: group.joinKey, joinHashHash });
    }
  }

  return hashedComputeGroups;
}
